import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { DeviceIDBadge } from '../../../shared/components/common/Badges';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import './TechnicianAnalytics.css';

/**
 * Technician fleet-health outlook (not personal throughput).
 * Owns: attention KPIs, alert patterns, devices needing attention, 30/60/90 schedule.
 */
function TechnicianAnalytics({
  token,
  user,
  onNavigateToAlerts,
  onNavigateToDevices,
  onNavigateToDeviceView,
  onNavigateToDashboard,
}) {
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [assignedDevices, setAssignedDevices] = useState([]);
  const [alertMetrics, setAlertMetrics] = useState(null);
  const [deviceHealthMetrics, setDeviceHealthMetrics] = useState({});
  const [ppmComplianceData, setPpmComplianceData] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    criticalAlerts: false,
    deviceHealth: false,
    ppmCompliance: false
  });
  const [activeMaintenanceTab, setActiveMaintenanceTab] = useState('ppm');
  const hasAppliedDefaultExpand = useRef(false);

  useEffect(() => {
    if (token && user) {
      fetchTechnicianAnalytics();
    }
  }, [token, user]);

  // Default-expand risk: critical alerts first, else devices needing attention
  useEffect(() => {
    if (loading || hasAppliedDefaultExpand.current) return;
    if (!assignedDevices.length && !alertMetrics) return;

    const critical = alertMetrics?.critical || 0;
    const attention =
      (deviceHealthMetrics.critical || 0) + (deviceHealthMetrics.warning || 0);

    setExpandedSections({
      criticalAlerts: critical > 0,
      deviceHealth: critical === 0 && attention > 0,
      ppmCompliance: false
    });
    hasAppliedDefaultExpand.current = true;
  }, [loading, assignedDevices.length, alertMetrics, deviceHealthMetrics]);

  const fetchTechnicianAnalytics = async () => {
    try {
      setLoading(true);
      setFetchError(null);

      const alertsResponse = await axios.get(API_ENDPOINTS.ALERTS.LIST + '?mode=full&no_pagination=true', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const backendAlerts = alertsResponse.data.alerts || [];

      const assignedResponse = await axios.get(API_ENDPOINTS.DEVICES.ASSIGNED_TO_ME, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const devices = assignedResponse.data.devices || [];
      const assignedIds = new Set(devices.map((d) => d.id).filter(Boolean));
      // Defense in depth: only alerts tied to this technician's assigned devices
      const scopedAlerts = backendAlerts.filter((alert) => {
        const deviceId =
          alert?.device_id ||
          (alert?.subject_type === 'device' ? alert.subject_id : null);
        return deviceId != null && assignedIds.has(deviceId);
      });

      setAssignedDevices(devices);
      calculateAnalytics(devices, scopedAlerts);
    } catch (error) {
      console.error('Error fetching technician analytics:', error);
      setAssignedDevices([]);
      setAlertMetrics(null);
      setDeviceHealthMetrics({});
      setPpmComplianceData({});
      setFetchError('Failed to load your device health outlook. Please try again.');
      toast.error('Failed to load assigned devices. You may not have any devices assigned yet.');
    } finally {
      setLoading(false);
    }
  };

  const calculateAnalytics = (devices, backendAlerts = []) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sortedAlerts = [...backendAlerts].sort((a, b) => {
      const priorityOrder = { critical: 0, warning: 1, info: 2 };
      const aPriority = priorityOrder[a.priority] || 2;
      const bPriority = priorityOrder[b.priority] || 2;

      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      return (b.days_overdue || 0) - (a.days_overdue || 0);
    });

    const metrics = {
      total: sortedAlerts.length,
      critical: sortedAlerts.filter(a => a.priority === 'critical').length,
      warning: sortedAlerts.filter(a => a.priority === 'warning').length,
      info: sortedAlerts.filter(a => a.priority === 'info').length,
      byType: {
        ppm: sortedAlerts.filter(a => a.type_display === 'PPM').length,
        calibration: sortedAlerts.filter(a => a.type_display === 'Calibration').length,
        warranty: sortedAlerts.filter(a => a.type_display === 'Warranty').length,
        contract: sortedAlerts.filter(a => a.type_display === 'Contract').length,
        compliance: sortedAlerts.filter(a => a.type_display === 'Compliance').length
      },
      daysOverdueDistribution: {
        overdue_0_to_7: sortedAlerts.filter(a => (a.days_overdue || 0) > 0 && (a.days_overdue || 0) <= 7).length,
        overdue_7_plus: sortedAlerts.filter(a => (a.days_overdue || 0) > 7).length
      },
      topPriority: sortedAlerts.slice(0, 5)
    };

    setAlertMetrics(metrics);

    if (!devices || devices.length === 0) {
      setDeviceHealthMetrics({});
      setPpmComplianceData({});
      return;
    }

    const healthMetrics = {
      total_devices: devices.length,
      healthy: 0,
      warning: 0,
      critical: 0,
      devices_by_status: []
    };

    devices.forEach(device => {
      let health_status = 'healthy';
      let health_score = 100;
      const issues = [];

      if (device.ppm_next_due_date) {
        const ppmDate = new Date(device.ppm_next_due_date);
        const daysUntilDue = Math.floor((ppmDate - today) / (1000 * 60 * 60 * 24));
        if (daysUntilDue < 0) {
          health_score -= 30;
          issues.push(`PPM ${Math.abs(daysUntilDue)} days overdue`);
          health_status = 'critical';
        } else if (daysUntilDue < 7) {
          health_score -= 15;
          issues.push(`PPM due in ${daysUntilDue} days`);
          if (health_status !== 'critical') health_status = 'warning';
        }
      }

      if (device.calibration_required === 'Yes' && device.calibration_next_due_date) {
        const calDate = new Date(device.calibration_next_due_date);
        const daysUntilDue = Math.floor((calDate - today) / (1000 * 60 * 60 * 24));
        if (daysUntilDue < 0) {
          health_score -= 20;
          issues.push(`Calibration ${Math.abs(daysUntilDue)} days overdue`);
          health_status = 'critical';
        } else if (daysUntilDue < 7) {
          health_score -= 10;
          issues.push(`Calibration due in ${daysUntilDue} days`);
          if (health_status !== 'critical') health_status = 'warning';
        }
      }

      if (device.warranty_expiry) {
        const warrantyDate = new Date(device.warranty_expiry);
        const daysUntilExpiry = Math.floor((warrantyDate - today) / (1000 * 60 * 60 * 24));
        if (daysUntilExpiry < 0) {
          health_score -= 10;
          issues.push('Warranty expired');
          if (health_status !== 'critical') health_status = 'warning';
        } else if (daysUntilExpiry < 30) {
          health_score -= 5;
          issues.push(`Warranty expires in ${daysUntilExpiry} days`);
          if (health_status !== 'critical') health_status = 'warning';
        }
      }

      health_score = Math.max(0, health_score);

      if (health_status === 'critical') healthMetrics.critical++;
      else if (health_status === 'warning') healthMetrics.warning++;
      else healthMetrics.healthy++;

      healthMetrics.devices_by_status.push({
        id: device.id,
        name: device.name,
        identifier: device.device_identifier,
        department: device.department,
        status: health_status,
        health_score,
        issues
      });
    });

    setDeviceHealthMetrics(healthMetrics);

    const ppmData = {
      total_devices_with_ppm: 0,
      overdue: 0,
      due_this_month: 0,
      due_this_quarter: 0,
      upcoming: 0,
      frequency_breakdown: {},
      devices: []
    };

    const calibrationData = {
      total_devices_with_calibration: 0,
      overdue: 0,
      due_this_month: 0,
      due_this_quarter: 0,
      upcoming: 0,
      frequency_breakdown: {},
      devices: []
    };

    const complianceData = {
      total_devices_requiring_compliance: 0,
      overdue: 0,
      due_this_month: 0,
      due_this_quarter: 0,
      upcoming: 0,
      by_type: {},
      frequency_breakdown: {},
      devices: []
    };

    const thirtyDaysLater = new Date(today);
    thirtyDaysLater.setDate(thirtyDaysLater.getDate() + 30);

    const ninetyDaysLater = new Date(today);
    ninetyDaysLater.setDate(ninetyDaysLater.getDate() + 90);

    devices.forEach(device => {
      if (device.ppm_frequency) {
        ppmData.total_devices_with_ppm++;

        const ppmDate = new Date(device.ppm_next_due_date || today);
        const daysUntilDue = Math.floor((ppmDate - today) / (1000 * 60 * 60 * 24));

        if (daysUntilDue < 0) {
          ppmData.overdue++;
        } else if (ppmDate <= thirtyDaysLater) {
          ppmData.due_this_month++;
        } else if (ppmDate <= ninetyDaysLater) {
          ppmData.due_this_quarter++;
        } else {
          ppmData.upcoming++;
        }

        if (!ppmData.frequency_breakdown[device.ppm_frequency]) {
          ppmData.frequency_breakdown[device.ppm_frequency] = 0;
        }
        ppmData.frequency_breakdown[device.ppm_frequency]++;

        ppmData.devices.push({
          id: device.id,
          name: device.name,
          identifier: device.device_identifier,
          frequency: device.ppm_frequency,
          last_done: device.ppm_last_done_date,
          next_due: device.ppm_next_due_date,
          status: daysUntilDue < 0 ? 'overdue' : daysUntilDue < 7 ? 'urgent' : 'scheduled'
        });
      }

      if (device.calibration_required === 'Yes' && device.calibration_next_due_date) {
        calibrationData.total_devices_with_calibration++;

        const calDate = new Date(device.calibration_next_due_date);
        const daysUntilDue = Math.floor((calDate - today) / (1000 * 60 * 60 * 24));

        if (daysUntilDue < 0) {
          calibrationData.overdue++;
        } else if (calDate <= thirtyDaysLater) {
          calibrationData.due_this_month++;
        } else if (calDate <= ninetyDaysLater) {
          calibrationData.due_this_quarter++;
        } else {
          calibrationData.upcoming++;
        }

        if (!calibrationData.frequency_breakdown[device.calibration_frequency]) {
          calibrationData.frequency_breakdown[device.calibration_frequency] = 0;
        }
        calibrationData.frequency_breakdown[device.calibration_frequency]++;

        calibrationData.devices.push({
          id: device.id,
          name: device.name,
          identifier: device.device_identifier,
          frequency: device.calibration_frequency,
          last_done: device.calibration_last_done_date,
          next_due: device.calibration_next_due_date,
          status: daysUntilDue < 0 ? 'overdue' : daysUntilDue < 7 ? 'urgent' : 'scheduled'
        });
      }

      if (device.compliance_required === 'Yes') {
        complianceData.total_devices_requiring_compliance++;

        const compDate = new Date(device.compliance_next_due_date || today);
        const daysUntilDue = Math.floor((compDate - today) / (1000 * 60 * 60 * 24));

        if (daysUntilDue < 0) {
          complianceData.overdue++;
        } else if (compDate <= thirtyDaysLater) {
          complianceData.due_this_month++;
        } else if (compDate <= ninetyDaysLater) {
          complianceData.due_this_quarter++;
        } else {
          complianceData.upcoming++;
        }

        const compType = device.compliance_type || 'Unknown';
        if (!complianceData.by_type[compType]) {
          complianceData.by_type[compType] = { total: 0, overdue: 0 };
        }
        complianceData.by_type[compType].total++;
        if (daysUntilDue < 0) {
          complianceData.by_type[compType].overdue++;
        }

        if (!complianceData.frequency_breakdown[device.compliance_frequency]) {
          complianceData.frequency_breakdown[device.compliance_frequency] = 0;
        }
        complianceData.frequency_breakdown[device.compliance_frequency]++;

        complianceData.devices.push({
          id: device.id,
          name: device.name,
          identifier: device.device_identifier,
          type: compType,
          frequency: device.compliance_frequency,
          last_done: device.compliance_last_done_date,
          next_due: device.compliance_next_due_date,
          status: daysUntilDue < 0 ? 'overdue' : daysUntilDue < 7 ? 'urgent' : 'scheduled'
        });
      }
    });

    setPpmComplianceData({ ppm: ppmData, calibration: calibrationData, compliance: complianceData });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'critical':
        return '🔴';
      case 'warning':
        return '🟠';
      case 'overdue':
        return '🔴';
      case 'urgent':
        return '🟠';
      case 'healthy':
        return '🟢';
      case 'scheduled':
        return '🟢';
      default:
        return '⚪';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'critical':
      case 'overdue':
        return '#d32f2f';
      case 'warning':
      case 'urgent':
        return '#f57c00';
      case 'healthy':
      case 'scheduled':
        return '#388e3c';
      default:
        return '#757575';
    }
  };

  const handleNavigateAlerts = () => {
    if (typeof onNavigateToAlerts === 'function') onNavigateToAlerts();
  };

  const handleNavigateDevices = () => {
    if (typeof onNavigateToDevices === 'function') onNavigateToDevices();
  };

  const handleNavigateDeviceView = (deviceId) => {
    if (deviceId && typeof onNavigateToDeviceView === 'function') {
      onNavigateToDeviceView(deviceId);
    }
  };

  const handleNavigateDashboard = () => {
    if (typeof onNavigateToDashboard === 'function') onNavigateToDashboard();
  };

  const handleUrgentAlertClick = (alert) => {
    // Match Alerts.js: device subject may expose device_id and/or subject_id
    const deviceId =
      alert?.device_id ||
      (alert?.subject_type === 'device' ? alert.subject_id : null);
    if (deviceId && typeof onNavigateToDeviceView === 'function') {
      onNavigateToDeviceView(deviceId);
      return;
    }
    handleNavigateAlerts();
  };

  const formatOverdueLabel = (days) => {
    const n = Number(days);
    if (!Number.isFinite(n) || n <= 0) return 'Due today';
    if (n === 1) return '1 day overdue';
    return `${n} days overdue`;
  };

  const renderSectionHeader = (sectionKey, title, badge = null) => (
    <button
      type="button"
      className="section-header"
      onClick={() => toggleSection(sectionKey)}
      aria-expanded={expandedSections[sectionKey]}
    >
      <span
        className={`section-toggle ${expandedSections[sectionKey] ? 'expanded' : ''}`}
        aria-hidden="true"
      >
        ▼
      </span>
      <h3>{title}</h3>
      {badge}
    </button>
  );

  const renderEmptyActions = () => (
    <div className="tech-analytics-empty-actions">
      {typeof onNavigateToDevices === 'function' && (
        <button type="button" className="tech-analytics-empty-action" onClick={handleNavigateDevices}>
          View My Devices
        </button>
      )}
      {typeof onNavigateToAlerts === 'function' && (
        <button type="button" className="tech-analytics-empty-action tech-analytics-empty-action--secondary" onClick={handleNavigateAlerts}>
          Open Alerts
        </button>
      )}
      {typeof onNavigateToDashboard === 'function' && (
        <button type="button" className="tech-analytics-empty-action tech-analytics-empty-action--secondary" onClick={handleNavigateDashboard}>
          Go to Dashboard
        </button>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="technician-analytics-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading your device health outlook...</p>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="technician-analytics-container">
        <div className="technician-analytics-description-section">
          <div className="tech-analytics-header-row">
            <h1>🖥️ My Device Health</h1>
            <button
              type="button"
              onClick={fetchTechnicianAnalytics}
              className="btn-refresh"
              title="Refresh"
              style={{ padding: '0.5rem', fontSize: '1.2rem', border: 'none', background: 'none', cursor: 'pointer', lineHeight: 1 }}
            >
              🔄
            </button>
          </div>
          <p>
            Fleet and schedule outlook for your assigned devices — not personal completion stats.
            {' '}
            {typeof onNavigateToDashboard === 'function' && (
              <button type="button" className="tech-analytics-inline-link" onClick={handleNavigateDashboard}>
                View My Performance on Dashboard
              </button>
            )}
          </p>
        </div>
        <div className="empty-state empty-state--error">
          <p>{fetchError}</p>
          <p className="empty-subtitle">Check your connection, then retry or open another page.</p>
          <div className="tech-analytics-empty-actions">
            <button type="button" className="tech-analytics-empty-action" onClick={fetchTechnicianAnalytics}>
              Retry
            </button>
          </div>
          {renderEmptyActions()}
        </div>
      </div>
    );
  }

  const attentionCount =
    (deviceHealthMetrics.critical || 0) + (deviceHealthMetrics.warning || 0);
  const attentionDevices =
    deviceHealthMetrics.devices_by_status?.filter(d => d.status !== 'healthy') || [];

  return (
    <div className="technician-analytics-container">
      <div className="technician-analytics-description-section">
        <div className="tech-analytics-header-row">
          <h1>🖥️ My Device Health</h1>
          <button
            type="button"
            onClick={fetchTechnicianAnalytics}
            className="btn-refresh"
            title="Refresh device health"
            style={{ padding: '0.5rem', fontSize: '1.2rem', border: 'none', background: 'none', cursor: 'pointer', lineHeight: 1 }}
          >
            🔄
          </button>
        </div>
        <p>
          What’s wrong with your assigned fleet, and what’s coming due beyond today — alerts, health risk, and 30/90-day schedule outlook.
          {' '}
          {typeof onNavigateToDashboard === 'function' && (
            <button type="button" className="tech-analytics-inline-link" onClick={handleNavigateDashboard}>
              View My Performance on Dashboard
            </button>
          )}
        </p>
      </div>

      {assignedDevices.length === 0 ? (
        <div className="empty-state">
          <p>No devices assigned to you yet.</p>
          <p className="empty-subtitle">
            Once devices are assigned, you’ll see attention KPIs, priority alerts, and schedule outlook here.
          </p>
          {renderEmptyActions()}
        </div>
      ) : (
        <>
          <div className="kpi-section">
            <button
              type="button"
              className={`kpi-card critical${typeof onNavigateToAlerts === 'function' ? ' kpi-card--clickable' : ''}`}
              onClick={handleNavigateAlerts}
              disabled={typeof onNavigateToAlerts !== 'function'}
            >
              <p className="kpi-value">{alertMetrics?.critical || 0}</p>
              <p className="kpi-label">Critical Issues</p>
              <p className="kpi-sublabel">Open Alerts</p>
            </button>
            <button
              type="button"
              className={`kpi-card ${attentionCount > 0 ? (deviceHealthMetrics.critical > 0 ? 'critical' : 'warning') : 'success'}${attentionCount > 0 ? ' kpi-card--clickable' : ''}`}
              onClick={() => {
                if (attentionCount > 0) {
                  setExpandedSections(prev => ({ ...prev, deviceHealth: true }));
                  const el = document.getElementById('tech-analytics-device-health');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              disabled={attentionCount === 0}
            >
              <p className="kpi-value">{attentionCount}</p>
              <p className="kpi-label">Devices Needing Attention</p>
              <p className="kpi-sublabel">
                {deviceHealthMetrics.critical || 0} critical, {deviceHealthMetrics.warning || 0} warning
              </p>
            </button>
            <button
              type="button"
              className={`kpi-card success${typeof onNavigateToDevices === 'function' ? ' kpi-card--clickable' : ''}`}
              onClick={handleNavigateDevices}
              disabled={typeof onNavigateToDevices !== 'function'}
            >
              <p className="kpi-value">{deviceHealthMetrics.healthy || 0}</p>
              <p className="kpi-label">Healthy Devices</p>
              <p className="kpi-sublabel">View My Devices</p>
            </button>
          </div>

          <section className="analytics-section alert-insights-section" id="tech-analytics-alerts">
            {renderSectionHeader(
              'criticalAlerts',
              '🚨 Priority Alerts',
              alertMetrics?.critical > 0 ? (
                <span className="alert-badge critical">{alertMetrics.critical}</span>
              ) : alertMetrics?.total > 0 ? (
                <span className="alert-badge">{alertMetrics.total}</span>
              ) : null
            )}

            {expandedSections.criticalAlerts && (
              <div className="section-content">
                {alertMetrics?.total === 0 ? (
                  <div className="no-data-message">
                    <p>✅ No alerts on your assigned devices.</p>
                    {typeof onNavigateToAlerts === 'function' && (
                      <button type="button" className="tech-analytics-empty-action tech-analytics-empty-action--secondary" onClick={handleNavigateAlerts}>
                        Open Alerts
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="alert-priority-strip">
                      <p className="alert-priority-summary">
                        <span>{alertMetrics.total} total</span>
                        <span className="alert-priority-dot" aria-hidden="true">·</span>
                        <span className="alert-priority-critical">{alertMetrics.critical || 0} critical</span>
                        <span className="alert-priority-dot" aria-hidden="true">·</span>
                        <span>{alertMetrics.warning || 0} warning</span>
                        {(alertMetrics.daysOverdueDistribution?.overdue_7_plus || 0) > 0 && (
                          <>
                            <span className="alert-priority-dot" aria-hidden="true">·</span>
                            <span className="alert-priority-critical">
                              {alertMetrics.daysOverdueDistribution.overdue_7_plus} overdue 7+ days
                            </span>
                          </>
                        )}
                      </p>
                      {typeof onNavigateToAlerts === 'function' && (
                        <button type="button" className="tech-analytics-inline-link" onClick={handleNavigateAlerts}>
                        Alerts ›
                        </button>
                      )}
                    </div>

                    {alertMetrics?.topPriority && alertMetrics.topPriority.length > 0 && (
                      <div className="top-urgent-section">
                        <h5 className="top-urgent-title">
                          Start here — top {Math.min(5, alertMetrics.topPriority.length)} most urgent
                        </h5>
                        <div className="devices-table-wrapper">
                          <table className="devices-health-table top-urgent-table">
                            <thead>
                              <tr>
                                <th scope="col" className="top-urgent-col-rank">#</th>
                                <th scope="col">Type</th>
                                <th scope="col">Device</th>
                                <th scope="col">Device ID</th>
                                <th scope="col">Category</th>
                                <th scope="col">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {alertMetrics.topPriority.map((alert, idx) => (
                                <tr
                                  key={alert.id || idx}
                                  className={`device-row top-urgent-row top-urgent-row--${alert.priority}${typeof onNavigateToDeviceView === 'function' || typeof onNavigateToAlerts === 'function' ? ' device-row--clickable' : ''}`}
                                  onClick={() => handleUrgentAlertClick(alert)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleUrgentAlertClick(alert);
                                    }
                                  }}
                                  tabIndex={0}
                                  role="button"
                                >
                                  <td className="top-urgent-col-rank">
                                    <span className="urgent-rank">{idx + 1}</span>
                                  </td>
                                  <td>
                                    <span className="urgent-type">{alert.type_display || '—'}</span>
                                  </td>
                                  <td className="device-name-cell">
                                    <span className="device-name-text">{alert.device_name || 'Unknown device'}</span>
                                  </td>
                                  <td className="device-id-cell">
                                    {alert.device_identifier ? (
                                      <DeviceIDBadge deviceId={alert.device_identifier} size="small" />
                                    ) : (
                                      <span className="urgent-category">—</span>
                                    )}
                                  </td>
                                  <td>
                                    <span className="urgent-category">{alert.category || '—'}</span>
                                  </td>
                                  <td>
                                    <span
                                      className={`urgent-days${(alert.days_overdue || 0) > 7 ? ' urgent-days--severe' : ''}`}
                                    >
                                      {formatOverdueLabel(alert.days_overdue)}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </section>

          <section className="analytics-section device-health-section" id="tech-analytics-device-health">
            {renderSectionHeader('deviceHealth', '💊 Devices Needing Attention')}

            {expandedSections.deviceHealth && (
              <div className="section-content">
                <div className="section-description">
                  <p>
                    Health is based on PPM, calibration, and warranty. Scores drop when work is overdue or due soon.
                    {' '}
                    {typeof onNavigateToDevices === 'function' && (
                      <button type="button" className="tech-analytics-inline-link" onClick={handleNavigateDevices}>
                        Browse all on My Devices
                      </button>
                    )}
                  </p>
                </div>

                {attentionDevices.length > 0 ? (
                  <div className="attention-needed-section">
                    <div className="devices-table-wrapper">
                      <table className="devices-health-table">
                        <thead>
                          <tr>
                            <th>Status</th>
                            <th>Device Name</th>
                            <th>Device ID</th>
                            <th>Health Score</th>
                            <th>Issues</th>
                          </tr>
                        </thead>
                        <tbody>
                          {attentionDevices.map(device => (
                            <tr
                              key={device.id}
                              className={`device-row device-${device.status}${typeof onNavigateToDeviceView === 'function' ? ' device-row--clickable' : ''}`}
                              onClick={() => handleNavigateDeviceView(device.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleNavigateDeviceView(device.id);
                                }
                              }}
                              tabIndex={typeof onNavigateToDeviceView === 'function' ? 0 : undefined}
                              role={typeof onNavigateToDeviceView === 'function' ? 'button' : undefined}
                            >
                              <td className="status-cell">
                                <span className="status-icon">{getStatusIcon(device.status)}</span>
                              </td>
                              <td className="device-name-cell">
                                <span className="device-name-text">{device.name}</span>
                              </td>
                              <td className="device-id-cell">
                                <DeviceIDBadge deviceId={device.identifier} size="small" />
                              </td>
                              <td className="health-score-cell">
                                <div className="health-score-circle" style={{ borderColor: getStatusColor(device.status) }}>
                                  <span>{device.health_score}</span>
                                </div>
                              </td>
                              <td className="issues-cell">
                                <div className="issues-list">
                                  {device.issues.map((issue, idx) => (
                                    <div key={idx} className="issue-badge">{issue}</div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="no-data-message">
                    <p>✅ All assigned devices look healthy right now.</p>
                    <p className="empty-subtitle">
                      {deviceHealthMetrics.healthy || 0} healthy device{(deviceHealthMetrics.healthy || 0) === 1 ? '' : 's'}.
                    </p>
                    {typeof onNavigateToDevices === 'function' && (
                      <button type="button" className="tech-analytics-empty-action" onClick={handleNavigateDevices}>
                        View My Devices
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </section>

          <section className="analytics-section ppm-compliance-section" id="tech-analytics-schedule">
            {renderSectionHeader('ppmCompliance', '🔧 Schedule & Compliance Outlook')}

            {expandedSections.ppmCompliance && (
              <div className="section-content">
                <div className="section-description">
                  <p>
                    Forward look (this month / quarter) for PPM, calibration, and compliance — beyond today’s Maintenance Due list.
                    {' '}
                    {typeof onNavigateToDashboard === 'function' && (
                      <button type="button" className="tech-analytics-inline-link" onClick={handleNavigateDashboard}>
                        Open Dashboard → Maintenance Due
                      </button>
                    )}
                  </p>
                </div>

                <div className="maintenance-tab-nav" role="tablist" aria-label="Schedule outlook tabs">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeMaintenanceTab === 'ppm'}
                    className={`tab-button ${activeMaintenanceTab === 'ppm' ? 'active' : ''}`}
                    onClick={() => setActiveMaintenanceTab('ppm')}
                  >
                    📋 Preventive Maintenance
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeMaintenanceTab === 'calibration'}
                    className={`tab-button ${activeMaintenanceTab === 'calibration' ? 'active' : ''}`}
                    onClick={() => setActiveMaintenanceTab('calibration')}
                  >
                    ⚙️ Calibration
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeMaintenanceTab === 'compliance'}
                    className={`tab-button ${activeMaintenanceTab === 'compliance' ? 'active' : ''}`}
                    onClick={() => setActiveMaintenanceTab('compliance')}
                  >
                    ✅ Regulatory Compliance
                  </button>
                </div>

                {activeMaintenanceTab === 'ppm' && (
                  <div className="tab-content" role="tabpanel">
                    <div className="tab ppm-tab">
                      <div className="status-summary">
                        <div className="status-item">
                          <span className="label">Total with PPM</span>
                          <span className="value">{ppmComplianceData.ppm?.total_devices_with_ppm || 0}</span>
                        </div>
                        <div className="status-item critical">
                          <span className="label">🔴 Overdue</span>
                          <span className="value">{ppmComplianceData.ppm?.overdue || 0}</span>
                        </div>
                        <div className="status-item warning">
                          <span className="label">🟠 Due this month</span>
                          <span className="value">{ppmComplianceData.ppm?.due_this_month || 0}</span>
                        </div>
                        <div className="status-item">
                          <span className="label">🟢 Due this quarter</span>
                          <span className="value">{ppmComplianceData.ppm?.due_this_quarter || 0}</span>
                        </div>
                      </div>

                      {ppmComplianceData.ppm?.frequency_breakdown && Object.keys(ppmComplianceData.ppm.frequency_breakdown).length > 0 && (
                        <div className="frequency-breakdown">
                          <h5>Frequency mix</h5>
                          {Object.entries(ppmComplianceData.ppm.frequency_breakdown).map(([freq, count]) => (
                            <div key={freq} className="frequency-item">
                              <span className="frequency-label">{freq}</span>
                              <div className="frequency-bar">
                                <div className="bar-fill" style={{ width: `${(count / ppmComplianceData.ppm.total_devices_with_ppm) * 100}%` }}></div>
                              </div>
                              <span className="frequency-count">{count}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {ppmComplianceData.ppm?.devices && ppmComplianceData.ppm.devices.length > 0 && (
                        <div className="devices-breakdown-section">
                          <h5>Device breakdown</h5>
                          <div className="devices-table-wrapper">
                            <table className="devices-maintenance-table">
                              <thead>
                                <tr>
                                  <th>Status</th>
                                  <th>Device Name</th>
                                  <th>Device ID</th>
                                  <th>Frequency</th>
                                  <th>Last Done</th>
                                  <th>Next Due</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ppmComplianceData.ppm.devices.map(device => (
                                  <tr
                                    key={`ppm-${device.id}`}
                                    className={`device-row status-${device.status}${typeof onNavigateToDeviceView === 'function' ? ' device-row--clickable' : ''}`}
                                    onClick={() => handleNavigateDeviceView(device.id)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleNavigateDeviceView(device.id);
                                      }
                                    }}
                                    tabIndex={typeof onNavigateToDeviceView === 'function' ? 0 : undefined}
                                    role={typeof onNavigateToDeviceView === 'function' ? 'button' : undefined}
                                  >
                                    <td className="status-cell">
                                      <span className="status-icon">{getStatusIcon(device.status)}</span>
                                    </td>
                                    <td className="device-name-cell">
                                      <span className="device-name-text">{device.name}</span>
                                    </td>
                                    <td className="device-id-cell">
                                      <DeviceIDBadge deviceId={device.identifier} size="small" />
                                    </td>
                                    <td className="frequency-cell">
                                      <span className="frequency-badge">{device.frequency}</span>
                                    </td>
                                    <td className="date-cell">
                                      {device.last_done ? new Date(device.last_done).toLocaleDateString() : 'Never'}
                                    </td>
                                    <td className="date-cell">
                                      {device.next_due ? new Date(device.next_due).toLocaleDateString() : 'Not scheduled'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeMaintenanceTab === 'calibration' && (
                  <div className="tab-content" role="tabpanel">
                    <div className="tab calibration-tab">
                      <div className="status-summary">
                        <div className="status-item">
                          <span className="label">Total with calibration</span>
                          <span className="value">{ppmComplianceData.calibration?.total_devices_with_calibration || 0}</span>
                        </div>
                        <div className="status-item critical">
                          <span className="label">🔴 Overdue</span>
                          <span className="value">{ppmComplianceData.calibration?.overdue || 0}</span>
                        </div>
                        <div className="status-item warning">
                          <span className="label">🟠 Due this month</span>
                          <span className="value">{ppmComplianceData.calibration?.due_this_month || 0}</span>
                        </div>
                        <div className="status-item">
                          <span className="label">🟢 Due this quarter</span>
                          <span className="value">{ppmComplianceData.calibration?.due_this_quarter || 0}</span>
                        </div>
                      </div>

                      {ppmComplianceData.calibration?.frequency_breakdown && Object.keys(ppmComplianceData.calibration.frequency_breakdown).length > 0 && (
                        <div className="frequency-breakdown">
                          <h5>Frequency mix</h5>
                          {Object.entries(ppmComplianceData.calibration.frequency_breakdown).map(([freq, count]) => (
                            <div key={freq} className="frequency-item">
                              <span className="frequency-label">{freq}</span>
                              <div className="frequency-bar">
                                <div className="bar-fill" style={{ width: `${(count / ppmComplianceData.calibration.total_devices_with_calibration) * 100}%` }}></div>
                              </div>
                              <span className="frequency-count">{count}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {ppmComplianceData.calibration?.devices && ppmComplianceData.calibration.devices.length > 0 && (
                        <div className="devices-breakdown-section">
                          <h5>Device breakdown</h5>
                          <div className="devices-table-wrapper">
                            <table className="devices-maintenance-table">
                              <thead>
                                <tr>
                                  <th>Status</th>
                                  <th>Device Name</th>
                                  <th>Device ID</th>
                                  <th>Frequency</th>
                                  <th>Last Done</th>
                                  <th>Next Due</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ppmComplianceData.calibration.devices.map(device => (
                                  <tr
                                    key={`cal-${device.id}`}
                                    className={`device-row status-${device.status}${typeof onNavigateToDeviceView === 'function' ? ' device-row--clickable' : ''}`}
                                    onClick={() => handleNavigateDeviceView(device.id)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleNavigateDeviceView(device.id);
                                      }
                                    }}
                                    tabIndex={typeof onNavigateToDeviceView === 'function' ? 0 : undefined}
                                    role={typeof onNavigateToDeviceView === 'function' ? 'button' : undefined}
                                  >
                                    <td className="status-cell">
                                      <span className="status-icon">{getStatusIcon(device.status)}</span>
                                    </td>
                                    <td className="device-name-cell">
                                      <span className="device-name-text">{device.name}</span>
                                    </td>
                                    <td className="device-id-cell">
                                      <DeviceIDBadge deviceId={device.identifier} size="small" />
                                    </td>
                                    <td className="frequency-cell">
                                      <span className="frequency-badge">{device.frequency}</span>
                                    </td>
                                    <td className="date-cell">
                                      {device.last_done ? new Date(device.last_done).toLocaleDateString() : 'Never'}
                                    </td>
                                    <td className="date-cell">
                                      {device.next_due ? new Date(device.next_due).toLocaleDateString() : 'Not scheduled'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeMaintenanceTab === 'compliance' && (
                  <div className="tab-content" role="tabpanel">
                    <div className="tab compliance-tab">
                      <div className="status-summary">
                        <div className="status-item">
                          <span className="label">Total requiring compliance</span>
                          <span className="value">{ppmComplianceData.compliance?.total_devices_requiring_compliance || 0}</span>
                        </div>
                        <div className="status-item critical">
                          <span className="label">🔴 Overdue</span>
                          <span className="value">{ppmComplianceData.compliance?.overdue || 0}</span>
                        </div>
                        <div className="status-item warning">
                          <span className="label">🟠 Due this month</span>
                          <span className="value">{ppmComplianceData.compliance?.due_this_month || 0}</span>
                        </div>
                        <div className="status-item">
                          <span className="label">🟢 Due this quarter</span>
                          <span className="value">{ppmComplianceData.compliance?.due_this_quarter || 0}</span>
                        </div>
                      </div>

                      {ppmComplianceData.compliance?.frequency_breakdown && Object.keys(ppmComplianceData.compliance.frequency_breakdown).length > 0 && (
                        <div className="frequency-breakdown">
                          <h5>Frequency mix</h5>
                          {Object.entries(ppmComplianceData.compliance.frequency_breakdown).map(([freq, count]) => (
                            <div key={freq} className="frequency-item">
                              <span className="frequency-label">{freq}</span>
                              <div className="frequency-bar">
                                <div className="bar-fill" style={{ width: `${(count / ppmComplianceData.compliance.total_devices_requiring_compliance) * 100}%` }}></div>
                              </div>
                              <span className="frequency-count">{count}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {ppmComplianceData.compliance?.by_type && Object.keys(ppmComplianceData.compliance.by_type).length > 0 && (
                        <div className="compliance-types-simple">
                          <h5>Compliance types</h5>
                          <div className="compliance-types-list">
                            {Object.entries(ppmComplianceData.compliance.by_type).map(([type, data]) => (
                              <div key={type} className="compliance-type-row">
                                <span className="compliance-type-name">{type}</span>
                                <span className="compliance-type-stats">{data.total} devices {data.overdue > 0 ? `(${data.overdue} overdue)` : ''}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {ppmComplianceData.compliance?.devices && ppmComplianceData.compliance.devices.length > 0 && (
                        <div className="devices-breakdown-section">
                          <h5>Device breakdown</h5>
                          <div className="devices-table-wrapper">
                            <table className="devices-maintenance-table">
                              <thead>
                                <tr>
                                  <th>Status</th>
                                  <th>Device Name</th>
                                  <th>Device ID</th>
                                  <th>Compliance Type</th>
                                  <th>Frequency</th>
                                  <th>Last Done</th>
                                  <th>Next Due</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ppmComplianceData.compliance.devices.map(device => (
                                  <tr
                                    key={`comp-${device.id}`}
                                    className={`device-row status-${device.status}${typeof onNavigateToDeviceView === 'function' ? ' device-row--clickable' : ''}`}
                                    onClick={() => handleNavigateDeviceView(device.id)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleNavigateDeviceView(device.id);
                                      }
                                    }}
                                    tabIndex={typeof onNavigateToDeviceView === 'function' ? 0 : undefined}
                                    role={typeof onNavigateToDeviceView === 'function' ? 'button' : undefined}
                                  >
                                    <td className="status-cell">
                                      <span className="status-icon">{getStatusIcon(device.status)}</span>
                                    </td>
                                    <td className="device-name-cell">
                                      <span className="device-name-text">{device.name}</span>
                                    </td>
                                    <td className="device-id-cell">
                                      <DeviceIDBadge deviceId={device.identifier} size="small" />
                                    </td>
                                    <td className="type-cell">
                                      <span className="type-badge">{device.type}</span>
                                    </td>
                                    <td className="frequency-cell">
                                      <span className="frequency-badge">{device.frequency}</span>
                                    </td>
                                    <td className="date-cell">
                                      {device.last_done ? new Date(device.last_done).toLocaleDateString() : 'Never'}
                                    </td>
                                    <td className="date-cell">
                                      {device.next_due ? new Date(device.next_due).toLocaleDateString() : 'Not scheduled'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default TechnicianAnalytics;
